import { Tabs } from "antd";
import { useAppDispatch, useAppSelector } from "core/utils";
import i18n from "i18next";
import { useEffect } from "react";

import { canAccess } from "../../../core/router/auth/permissions";
import { clearDefaultActiveKey } from "../../../core/setup/redux/state/tabs/tabState";
import type { StoreState } from "../../../core/setup/redux/store";
import type { Entity } from "../../../core/types/fe";
import { FormFieldRender } from "../components";
import { isFieldVisible } from "../components/ConditionalWrapper/ConditionalWrapper";
import { createHorizontalFormField } from "../formFieldLabelPositioning/Horizontal/CreateHorizontalFormField";
import type { FormLayoutProps } from "../types";
import { FormComponent, type FormField } from "../types/types";
import { FormTab } from "./styles/LayoutComponent";

export const TabsFormLayout = <T extends Entity>(props: FormLayoutProps<T>) => {
    const { formDefinition, formData, onChange } = props;
    const dispatch = useAppDispatch();
    const defaultActiveKeyFromRedux = useAppSelector((state: StoreState) => state.activeTab?.defaultActiveKey);

    // Clear the stored defaultActiveKey once after mount to avoid sticky tabs across forms
    useEffect(() => {
        if (defaultActiveKeyFromRedux) {
            dispatch(clearDefaultActiveKey());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canWrite = canAccess(formDefinition.resource, "canWrite");
    const canBasicWrite = canAccess(formDefinition.resource, "basicTabsWrite");
    const canBasicRead = canAccess(formDefinition.resource, "basicTabsRead");
    const canDynPropsTabsRead = canAccess(formDefinition.resource, "dynPropsTabsRead");

    let accessibleTabs = [];

    if (canWrite) {
        // @ts-ignore
        accessibleTabs = formDefinition?.tabs ?? [];
    } else if (canBasicWrite) {
        accessibleTabs = formDefinition?.tabs.filter((tab) => tab.name === "basicParameters");
    } else if (canBasicRead) {
        accessibleTabs = formDefinition?.tabs.filter((tab) => tab.name === "basicParameters");
    }

    if (!canDynPropsTabsRead) {
        accessibleTabs = accessibleTabs.filter((tab) => tab.name !== "dynamicForm.propertyGroupCategory");
    }

    const tabs = accessibleTabs.map((tab) => {
        // If the user has not reading access to this tab it will not even be rendered.
        const userHasReadAccess = canAccess(formDefinition.resource, "canRead");
        if (!userHasReadAccess) return null;

        const key = `${formDefinition.resource}_${tab.name}`;
        return (
            <Tabs.TabPane key={key} tab={i18n.t(`form.tab.${tab.name}`)} forceRender>
                {tab.fields.map((field: FormField) => {
                    // Skip ReadOnlyCheckbox fields
                    if (field.component === FormComponent.READ_ONLY_CHECK) {
                        return null;
                    }

                    if (field.attributes?.visible === false) {
                        return null;
                    }

                    // HANDLE READ ONLY
                    // If the user doesn't have writing access on this tab the fields inside this tab are disabled (read-only).
                    const formIsReadOnly = !canAccess(formDefinition.resource, "canWrite");
                    // OR, if the field can be disabled by a higher rule, set in field's disableIf attribute.
                    const disabledByOtherField = field.disableIf ? !!formData[field.disableIf as keyof T] : false;

                    // OR, if the field is disabled from a RO property in another profile.
                    // e.g. RO property set in Terminal Profiles, the field in Terminals should be disabled
                    const disabledByROInProfile = !!formData[`${field.name}CheckReadOnly` as keyof T];

                    const readOnly =
                        disabledByROInProfile || formIsReadOnly || (field.attributes?.readOnly ?? false) || disabledByOtherField;

                    // finds the corresponding ReadOnlyCheck field when a readOnlyName is set in attributes
                    // @ts-ignore
                    const readOnlyFieldConfig = tab.fields.find((fieldConfig) => fieldConfig.name === field.attributes?.readOnlyName);

                    const hasVisibility = (currentField: FormField) => isFieldVisible(formData, currentField?.attributes);
                    const isVisible = hasVisibility(field) && !field.component.startsWith("Hidden");

                    return (
                        <FormFieldRender
                            key={field.name}
                            field={field}
                            factory={createHorizontalFormField}
                            readOnly={readOnly}
                            isVisible={isVisible}
                            onChange={onChange}
                            readOnlyField={readOnlyFieldConfig}
                        />
                    );
                })}
            </Tabs.TabPane>
        );
    });

    const defaultActiveKey = defaultActiveKeyFromRedux ?? undefined;

    return <FormTab defaultActiveKey={defaultActiveKey}>{tabs}</FormTab>;
};
